/**
 * The mobile PageSpeed score over the last half year, one point a week, over
 * Lighthouse's three bands (poor below 50, needs work to 89, good from 90).
 *
 * Mobile only: it is the harder test and the one the drop notice watches, and
 * two lines on a chart this small would be read as one. Drawn only from two
 * points up; one point is not a trend.
 */
import { CartesianGrid, Line, LineChart, ReferenceArea, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@wa/ui";
import { mobileTrend, type SpeedHistoryPoint } from "../../../../lib/monitoringSpeed";
import type { T } from "../words";

// Estonian shapes, like every CRM date (`crm/ui/format.ts`).
const day = (ms: number) => new Date(ms).toLocaleDateString("et-EE", { day: "numeric", month: "numeric" });

const BANDS = [
  { from: 0, to: 50, fill: "var(--color-destructive)" },
  { from: 50, to: 90, fill: "var(--color-warning)" },
  { from: 90, to: 100, fill: "var(--color-success)" },
] as const;

export function SpeedTrend({ history, t }: { history: SpeedHistoryPoint[]; t: T }) {
  const points = mobileTrend(history);
  if (points.length < 2) return null;

  const config = { score: { label: t("monitoring.speed.trendSeries"), color: "var(--color-primary)" } } satisfies ChartConfig;

  return (
    <div className="px-[26px] pt-5">
      <p className="crm-prow-n mb-2">{t("monitoring.speed.trendTitle")}</p>
      <ChartContainer config={config} className="aspect-auto w-full" style={{ height: 150 }}>
        <LineChart data={points} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
          {BANDS.map((b) => (
            <ReferenceArea key={b.from} y1={b.from} y2={b.to} fill={b.fill} fillOpacity={0.07} strokeOpacity={0} ifOverflow="hidden" />
          ))}
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={day}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
          />
          {/* The two band edges; 100 is the top of the chart and would crowd 90 at this height. */}
          <YAxis domain={[0, 100]} ticks={[0, 50, 90]} interval={0} width={32} tickLine={false} axisLine={false} allowDecimals={false} />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                labelFormatter={(_, payload) => {
                  const at = payload?.[0]?.payload?.t;
                  return typeof at === "number" ? day(at) : "";
                }}
                formatter={(value) => <span className="font-mono font-medium tabular-nums text-foreground">{Number(value)}</span>}
              />
            }
          />
          <Line
            dataKey="score"
            type="monotone"
            stroke="var(--color-score)"
            strokeWidth={1.8}
            dot={{ r: 2.5, fill: "var(--color-score)" }}
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>
    </div>
  );
}
